import PropTypes from 'prop-types';
import React from 'react';
import { noop, debounce } from 'lodash';

import { LocaleService, WindowProxy, UrlService } from '@sm360/widget-commons-core-services';

import { InputDropDown } from '@sm360/widget-commons-forms';
import {
    AnalyticsService,
    TRACKING_TAGS as TAGS,
    ADOBE_TAGS,
    AdobeService,
    ADOBE_VARIANT_CONSTANTS,
} from '@sm360/widget-commons-general';

import PaymentOptionsModel from 'commons-vehicle/Vehicle/models/PaymentOptionsModel.js';
import TradeInVehicleModel from 'commons-vehicle/Vehicle/models/TradeInVehicleModel.js';

import InitialCashDown from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/InitialCashDown/InitialCashDown.jsx';
import MethodButton from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/MethodButton/MethodButton.jsx';
import TradeInValue from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/TradeInValue/TradeInValue.jsx';

import 'commons-vehicle/Vehicle/components/PaymentOptionFilters/PaymentOptionFilters.scss';
import InventoryTypeConstants from 'commons-vehicle/constants/inventoryType.js';

const possibleFrequency = {
    CASHDOWN: 'cashdown',
};
class PaymentOptionFilters extends React.Component {
    constructor(props) {
        super(props);

        const { maximumTerm, defaultTerm, preferredPaymentFrequency, purchaseMethod } = this.props.paymentOptions;

        // To validate if maximum term has been selected
        const isMaximumTermAvailable = UrlService.getUrlParameter('maximumTerm');
        const finalTerm = defaultTerm === null || defaultTerm === '' || defaultTerm === 0 ? maximumTerm : defaultTerm;

        this.state = {
            paymentOpened: false,
            currentMethod: purchaseMethod,
            purchaseMethodSort: {
                value: preferredPaymentFrequency,
                label: preferredPaymentFrequency,
            },
            paymentTypeSelection: {
                value: possibleFrequency.CASHDOWN,
                label: LocaleService.t('FILTERS.INITIAL_CASHDOWN'),
            },
            selectedTerms: {
                value: isMaximumTermAvailable ? maximumTerm : finalTerm,
                label: isMaximumTermAvailable ? maximumTerm : finalTerm,
            },
        };

        this.handleCollapse = this.handleCollapse.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handlePaymentChange = this.handlePaymentChange.bind(this);
        this.openCashdown = this.openCashdown.bind(this);
        this.selectMethod = this.handleMethodSelection.bind(this);
        this.handleMethodSelection = this.handleMethodSelection.bind(this);
        this.handleTradeInChange = this.handleTradeInChange.bind(this);
        this.handleOweOnTradeChange = this.handleOweOnTradeChange.bind(this);
        this.handleCashDownChange = this.handleCashDownChange.bind(this);
        this.handleTargetPaymentChange = this.handleTargetPaymentChange.bind(this);
        this.tradeInAnalytics = this.tradeInAnalytics.bind(this);
        this.debounceTradeInAnalytics = debounce(this.tradeInAnalytics, 5000);
        this.oweOnTradeAnalytics = this.oweOnTradeAnalytics.bind(this);
        this.debounceOweOnTradeAnalytics = debounce(this.oweOnTradeAnalytics, 5000);
        this.cashDownAnalytics = this.cashDownAnalytics.bind(this);
        this.debounceCashDownAnalytics = debounce(this.cashDownAnalytics, 5000);
        this.targetPaymentAnalytics = this.targetPaymentAnalytics.bind(this);
        this.debounceTargetPaymentAnalytics = debounce(this.targetPaymentAnalytics, 5000);
    }

    componentWillReceiveProps(newProps) {
        if (
            this.props.hasLease !== newProps.hasLease ||
            this.props.paymentOptions !== newProps.paymentOptions ||
            this.props.tradeInVehicle !== newProps.tradeInVehicle
        ) {
            const { maximumTerm, defaultTerm, preferredPaymentFrequency, purchaseMethod } = newProps.paymentOptions;

            // To validate if maximum term has been selected
            const isMaximumTermAvailable = UrlService.getUrlParameter('maximumTerm');
            const finalTerm =
                defaultTerm === null || defaultTerm === '' || defaultTerm === 0 ? maximumTerm : defaultTerm;

            this.setState({
                currentMethod: purchaseMethod,
                purchaseMethodSort: {
                    value: preferredPaymentFrequency,
                    label: preferredPaymentFrequency,
                },
                paymentTypeSelection: {
                    value: possibleFrequency.CASHDOWN,
                    label: LocaleService.t('FILTERS.INITIAL_CASHDOWN'),
                },
                selectedTerms: {
                    value: isMaximumTermAvailable ? maximumTerm : finalTerm,
                    label: isMaximumTermAvailable ? maximumTerm : finalTerm,
                },
            });
        }
    }

    openCashdown() {
        if (this.state.paymentTypeSelection.value === possibleFrequency.CASHDOWN) {
            this.props.actions.setTargetPayment(0);
            this.props.actions.setCashDown(0);
        }
    }

    handleCollapse() {
        const windowProxy = WindowProxy.get();
        this.setState(
            (oldState) => {
                return {
                    paymentOpened: !oldState.paymentOpened,
                };
            },
            () => {
                if (windowProxy) {
                    windowProxy.document.body.classList.toggle('paymentPanelOpen-no-scroll', this.state.paymentOpened);
                }
            }
        );
    }

    handleInputChange(inputValue, name) {
        let inputs;
        if (name === 'purchaseMethodSort') {
            inputs = this.state.purchaseMethodSort;
        } else if (name === 'selectTerms') {
            inputs = this.state.selectedTerms;
        }
        if (inputs.value !== inputValue.value) {
            inputs.value = inputValue.value;
            inputs.label = inputValue.label;

            if (name === 'purchaseMethodSort') {
                this.props.actions.selectPreferredPaymentFrequency(inputValue.value);
                AnalyticsService.ga4Trigger(TAGS.PREFERRED_FREQUENCY(inputValue.label));
            } else if (name === 'selectTerms') {
                this.props.actions.selectMaximumTerm(inputValue.value);
            }
        }
    }

    handlePaymentChange() {
        this.setState(() => this.openCashdown());
    }

    handleMethodSelection(paymentMethod) {
        if (this.props.hasAdobeTagging) {
            if (this.props.colanderGroupSlug === 'hub_commercial') {
                // This is used commercial inventory
                AdobeService.eventTrigger(
                    ADOBE_TAGS.COMMERCIAL_SEARCH_RESULT_PAGE_OTHER_ACTIONS(InventoryTypeConstants.USED, paymentMethod),
                    ADOBE_VARIANT_CONSTANTS.GLOBAL_ONCLICK
                );
            } else if (
                this.props.colanderGroupSlug === 'hub_new' &&
                this.props.inventoryType === InventoryTypeConstants.COMMERCIAL
            ) {
                // This is new commercial inventory
                AdobeService.eventTrigger(
                    ADOBE_TAGS.COMMERCIAL_SEARCH_RESULT_PAGE_OTHER_ACTIONS(InventoryTypeConstants.NEW, paymentMethod),
                    ADOBE_VARIANT_CONSTANTS.GLOBAL_ONCLICK
                );
            } else {
                // This is either new or used inventory
                AdobeService.eventTrigger(
                    ADOBE_TAGS.SEARCH_RESULT_PAGE_OTHER_ACTIONS(
                        this.props.colanderGroupSlug === 'hub_new' ? 'New' : 'Used',
                        paymentMethod
                    ),
                    ADOBE_VARIANT_CONSTANTS.GLOBAL_ONCLICK
                );
            }
        }
        AnalyticsService.ga4Trigger(TAGS.PAYMENT_TYPE(paymentMethod));
        this.setState({
            currentMethod: paymentMethod,
        });
    }

    tradeInAnalytics(value) {
        if (value) AnalyticsService.ga4Trigger(TAGS.TRADE_IN_VALUE(value));
    }

    oweOnTradeAnalytics(value) {
        if (value) AnalyticsService.ga4Trigger(TAGS.OWE_ON_TRADE(value));
    }

    cashDownAnalytics(value) {
        if (value) AnalyticsService.ga4Trigger(TAGS.INITIAL_CASH_DOWN(value));
    }

    targetPaymentAnalytics(value) {
        if (value) AnalyticsService.ga4Trigger(TAGS.TARGET_PAYMENT(value));
    }

    handleTradeInChange(value) {
        const storage = WindowProxy.localStorage();
        const valueFromLocalStorage = storage && parseInt(storage.getItem('tradeIn'), 10);
        if (value !== valueFromLocalStorage) this.debounceTradeInAnalytics(value);
        this.props.actions.setTradeIn(value);
    }

    handleOweOnTradeChange(value) {
        const storage = WindowProxy.localStorage();
        const valueFromLocalStorage = storage && parseInt(storage.getItem('lien'), 10);
        if (value !== valueFromLocalStorage) this.debounceOweOnTradeAnalytics(value);
        this.props.actions.setLien(value);
    }

    handleCashDownChange(value) {
        this.props.actions.setCashDown(value);
        this.debounceCashDownAnalytics(value);
    }

    handleTargetPaymentChange(value) {
        // this.props.actions.setTargetPayment(value);
        this.debounceTargetPaymentAnalytics(value);
    }

    render() {
        const { paymentOpened, purchaseMethodSort, selectedTerms } = this.state;
        const emptyFrequencies = this.props.paymentOptions.frequencyValues?.length === 0;
        const emptyTerms = this.props.paymentOptions.terms?.length === 0;

        const paymentFrequenciesButtons = emptyFrequencies
            ? [
                  {
                      value: purchaseMethodSort.value,
                      label: LocaleService.t('FILTERS.NOT_AVAILABLE'),
                  },
              ]
            : this.props.paymentOptions.frequencyValues.map((paymentFrequency) => ({
                  value: paymentFrequency.value,
                  label: paymentFrequency.label,
              }));

        const paymentTypeOptions = [
            {
                value: possibleFrequency.CASHDOWN,
                label: LocaleService.t('FILTERS.INITIAL_CASHDOWN'),
            },
        ];

        const termsButtons = emptyTerms
            ? [{ value: selectedTerms.value, label: LocaleService.t('FILTERS.NOT_AVAILABLE') }]
            : this.props.paymentOptions.terms.map((term) => ({
                  value: term,
                  label: LocaleService.t('MONTH_TERMS', { terms: term }),
              }));

        const paymentClass = ['listing-used-payment-wrap', this.state.currentMethod];
        const paymentBloc = (
            <div className={paymentClass.join(' ')}>
                <button
                    className="listing-used-filters-head-mobile-close picto-showroom icon-X-rouge"
                    onClick={this.handleCollapse}
                />
                <MethodButton
                    selectPreferredPurchaseMethod={this.props.actions.selectPreferredPurchaseMethod}
                    hasLease={this.props.hasLease}
                    currentMethod={this.state.currentMethod}
                    onSelection={this.handleMethodSelection}
                />
                <div className="listing-used-payment-wrap-range">
                    <InputDropDown
                        name="paymentTypeSelect"
                        value={this.state.paymentTypeSelection.value}
                        options={paymentTypeOptions}
                        placeholder={LocaleService.t('FILTERS.PAYMENT_SELECTION')}
                        valueChange={this.handlePaymentChange}
                        classNames={['input-dropdown not-empty payment-selector']}
                    />
                    <InitialCashDown
                        initialCashDown={this.props.paymentOptions.initialCashDown}
                        setInitialCashDown={this.props.actions.setInitialCashDown}
                        setCashDown={this.handleCashDownChange}
                        disabled={emptyTerms || emptyFrequencies}
                    />
                </div>
                <div className="listing-used-payment-wrap-range">
                    <div className="listing-used-payment-frequency">
                        <InputDropDown
                            name="purchaseMethodSort"
                            value={this.state.purchaseMethodSort.value}
                            options={paymentFrequenciesButtons}
                            placeholder={LocaleService.t('PREFERRED_FREQUENCY')}
                            valueChange={this.handleInputChange}
                            classNames={['input-dropdown not-empty']}
                            disabled={emptyFrequencies}
                        />
                    </div>

                    <div className="listing-used-max-term">
                        <InputDropDown
                            name="selectTerms"
                            value={this.state.selectedTerms.value}
                            options={termsButtons}
                            placeholder={LocaleService.t('MAXIMUM_TERM')}
                            valueChange={this.handleInputChange}
                            classNames={['input-dropdown not-empty']}
                            disabled={emptyTerms}
                        />
                    </div>
                </div>
                <TradeInValue
                    tradeInVehicle={this.props.tradeInVehicle}
                    setTradeIn={this.handleTradeInChange}
                    setLien={this.handleOweOnTradeChange}
                    setTargetPayment={this.props.actions.setTargetPayment}
                    initialTargetPayment={this.props.paymentOptions.initialTargetPayment}
                    saveTradeInVehicle={this.props.tradeInActions.saveTradeInVehicle}
                />
            </div>
        );

        const userPaymentClass = ['listing-used-payment clearfix'];
        if (paymentOpened) {
            userPaymentClass.push('payment-open');
        }

        return (
            <div className={userPaymentClass.join(' ')}>
                <div className="listing-used-set-budget">
                    <button className="picto-showroom-after" onClick={this.handleCollapse}>
                        {LocaleService.t('SET_MY_BUDGET')}
                    </button>
                </div>
                {paymentBloc}
            </div>
        );
    }
}

PaymentOptionFilters.defaultProps = {
    hasLease: false,
    tradeInVehicle: null,
    paymentOptions: new PaymentOptionsModel(),
    actions: {
        filterPayments: noop,
        selectMaximumTerm: noop,
        selectPreferredPaymentFrequency: noop,
        selectPreferredPurchaseMethod: noop,
        selectPurchaseMethod: noop,
        setCashDown: noop,
        setInitialCashDown: noop,
        setInitialTargetPayment: noop,
        setLien: noop,
        setPaymentRange: noop,
        setTargetPayment: noop,
        setTradeIn: noop,
    },
    tradeInActions: {
        saveTradeInVehicle: noop,
    },
    inventoryType: '',
    hasAdobeTagging: false,
};

PaymentOptionFilters.propTypes = {
    actions: PropTypes.shape({
        filterPayments: PropTypes.func.isRequired,
        selectMaximumTerm: PropTypes.func.isRequired,
        selectPreferredPaymentFrequency: PropTypes.func.isRequired,
        selectPreferredPurchaseMethod: PropTypes.func.isRequired,
        selectPurchaseMethod: PropTypes.func.isRequired,
        setCashDown: PropTypes.func.isRequired,
        setInitialCashDown: PropTypes.func.isRequired,
        setInitialTargetPayment: PropTypes.func.isRequired,
        setLien: PropTypes.func.isRequired,
        setPaymentRange: PropTypes.func.isRequired,
        setTargetPayment: PropTypes.func.isRequired,
        setTradeIn: PropTypes.func.isRequired,
    }).isRequired,
    hasLease: PropTypes.bool,
    paymentOptions: PropTypes.instanceOf(PaymentOptionsModel).isRequired,
    tradeInActions: PropTypes.shape({
        saveTradeInVehicle: PropTypes.func.isRequired,
    }).isRequired,
    tradeInVehicle: PropTypes.instanceOf(TradeInVehicleModel),
    inventoryType: PropTypes.string.isRequired,
    hasAdobeTagging: PropTypes.bool,
};

PaymentOptionFilters.displayName = 'PaymentOptionFilters';

export default PaymentOptionFilters;
